import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '../../assets/icons/search.svg?react';
import { PatientRoomPropertyControllerApi } from '../../api/generated';
import type { PatientRoomPropertyUsageResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { setRoomProperties as setRoomPropertiesCache } from '../../store/roomProperties/roomPropertiesSlice';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { Table, useTableSort } from '../UI';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { DeleteRoomNeedModal } from './DeleteRoomNeedModal';
import { FooterCell } from './FooterCell';
import { getRoomNeedsColumns } from './roomNeedsColumns';
import { DEFAULT_ROOM_NEEDS_SORT } from './roomNeedsConstants';
import styles from './RoomNeedsSettings.module.css';

const NEW_PROPERTY_ID = 'new' as const;
type EditingPropertyId = string | null;

export const RoomNeedsSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [roomProperties, setRoomProperties] = useState<PatientRoomPropertyUsageResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { sort, handleSortChange } = useTableSort(DEFAULT_ROOM_NEEDS_SORT);
  const [editingPropertyId, setEditingPropertyId] = useState<EditingPropertyId>(null);
  const [editingName, setEditingName] = useState('');
  const [editingComment, setEditingComment] = useState('');
  const [propertyToDelete, setPropertyToDelete] = useState<PatientRoomPropertyUsageResponse | null>(
    null
  );

  const refreshRoomProperties = useCallback(async () => {
    try {
      const config = createApiConfiguration();
      const api = new PatientRoomPropertyControllerApi(config);
      const response = await api.listPatientRoomPropertiesWithUsage();
      setRoomProperties(response.data);
      // Write through to the shared cache the patient form reads: its
      // fetch-if-empty hook never refetches, so a create/rename/delete here
      // would not reach the room-need picker until a reload. Mapped down to the
      // slice's {id, name} shape, matching fetchRoomProperties.
      dispatch(setRoomPropertiesCache(response.data.map(({ id, name }) => ({ id, name }))));
    } catch (error) {
      console.error('Failed to fetch room properties:', error);
      dispatch(showError({ message: t('fetch-error') }));
    }
  }, [dispatch, t]);

  useEffect(() => {
    const init = async () => {
      await refreshRoomProperties();
    };
    void init();
  }, [refreshRoomProperties]);

  const filteredProperties = useMemo(() => {
    if (!searchQuery) return roomProperties;
    const q = searchQuery.toLowerCase();
    return roomProperties.filter((p) => p.name.toLowerCase().includes(q));
  }, [roomProperties, searchQuery]);

  const effectiveEditingPropertyId: EditingPropertyId =
    editingPropertyId === null || editingPropertyId === NEW_PROPERTY_ID
      ? editingPropertyId
      : filteredProperties.some((p) => p.id === editingPropertyId)
        ? editingPropertyId
        : null;

  const startEdit = useCallback((property: PatientRoomPropertyUsageResponse) => {
    setEditingPropertyId(property.id);
    setEditingName(property.name);
    setEditingComment(property.description ?? '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingPropertyId(null);
    setEditingName('');
    setEditingComment('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!effectiveEditingPropertyId || effectiveEditingPropertyId === NEW_PROPERTY_ID) return;
    const trimmedName = editingName.trim();
    if (trimmedName.length === 0) return;
    const propertyId = effectiveEditingPropertyId;
    try {
      const config = createApiConfiguration();
      const api = new PatientRoomPropertyControllerApi(config);
      await api.updatePatientRoomProperty({
        propertyId,
        updatePatientRoomPropertyRequest: {
          name: trimmedName,
          description: editingComment || undefined
        }
      });
      setEditingPropertyId(null);
      setEditingName('');
      setEditingComment('');
      await refreshRoomProperties();
    } catch (error) {
      console.error('Failed to update room property:', error);
      dispatch(showError({ message: t('update-error') }));
    }
  }, [effectiveEditingPropertyId, editingName, editingComment, refreshRoomProperties, dispatch, t]);

  const createProperty = useCallback(
    async (name: string) => {
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomPropertyControllerApi(config);
        await api.createPatientRoomProperty({
          createPatientRoomPropertyRequest: { name }
        });
        setEditingPropertyId(null);
        await refreshRoomProperties();
      } catch (error) {
        console.error('Failed to create room property:', error);
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [refreshRoomProperties, dispatch, t]
  );

  const handleDeleted = useCallback(() => {
    setPropertyToDelete(null);
    void refreshRoomProperties();
  }, [refreshRoomProperties]);

  const columns = useMemo(
    () =>
      getRoomNeedsColumns(t, {
        editingPropertyId: effectiveEditingPropertyId,
        editingName,
        editingComment,
        onStartEdit: startEdit,
        onNameChange: setEditingName,
        onCommentChange: setEditingComment,
        onSave: () => {
          void saveEdit();
        },
        onCancel: cancelEdit,
        onDelete: (property) => setPropertyToDelete(property)
      }),
    [t, effectiveEditingPropertyId, editingName, editingComment, startEdit, saveEdit, cancelEdit]
  );

  return (
    <section>
      <SectionHeader title={t('settings-room-needs-header')}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{t('settings-search-room-need')}</label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('room-needs-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      <Table
        columns={columns}
        data={filteredProperties}
        sort={sort}
        onSortChange={handleSortChange}
        getRowKey={(property) => property.id}
        footer={
          <FooterCell
            isEditing={editingPropertyId === NEW_PROPERTY_ID}
            onStartEdit={() => {
              setEditingPropertyId(NEW_PROPERTY_ID);
              setEditingName('');
              setEditingComment('');
            }}
            onSave={(name) => void createProperty(name)}
            onCancel={cancelEdit}
          />
        }
      />

      {propertyToDelete && (
        <DeleteRoomNeedModal
          property={propertyToDelete}
          onClose={() => setPropertyToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
};
